import React, { useState, useEffect } from 'react';

/**
 * DynamicFilters — category-aware filter sidebar for the storefront.
 *
 * Loads filterable field definitions for the active category from
 * GET /api/v1/categories/fields/:categoryId and renders the appropriate
 * filter control for each field (range, multi-select, checkbox, discrete).
 *
 * Props:
 *   categoryId      — slug/id from categories API (e.g. "food-beverage", "amplifiers")
 *   activeFilters   — { [fieldName]: value } — current active filter values
 *   onFilterChange  — (fieldName, value) => void
 *   onClearAll      — () => void
 *   style           — optional style overrides for the container
 */
const DynamicFilters = ({ categoryId, activeFilters = {}, onFilterChange, onClearAll, style }) => {
  const [fields, setFields] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set(['price']));
  const [rangeValues, setRangeValues] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!categoryId) {
      setFields([]);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        // Try specific category first, fall back to full list
        const base = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${base}/api/v1/categories/fields/${categoryId}`);
        if (res.ok) {
          const data = await res.json();
          const cat = data.data;
          if (cat && cat.fields) {
            const filterable = [
              ...(cat.fields.required || []),
              ...(cat.fields.recommended || []),
              ...(cat.fields.optional || []),
            ].filter(f => f.isFilterable);
            setFields(filterable);
          } else {
            setFields([]);
          }
        } else {
          setFields([]);
        }
      } catch {
        setFields([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [categoryId]);

  // Expand sections that have active filters
  useEffect(() => {
    const activeIds = Object.keys(activeFilters).filter(k => {
      const val = activeFilters[k];
      if (val === undefined || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      return true;
    });
    setExpandedSections(prev => new Set([...Array.from(prev), ...activeIds]));
  }, [activeFilters]);

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleMultiSelect = (fieldName, value, checked) => {
    const current = activeFilters[fieldName] || [];
    onFilterChange(fieldName, checked ? [...current, value] : current.filter(v => v !== value));
  };

  const handleRangeChange = (fieldName, type, value) => {
    setRangeValues(prev => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), [type]: value },
    }));
  };

  const applyRange = (fieldName) => {
    const range = rangeValues[fieldName];
    if (range) onFilterChange(fieldName, range);
  };

  const activeCount = Object.entries(activeFilters).filter(([, val]) => {
    if (val === undefined || val === null) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object') return val.min !== undefined || val.max !== undefined;
    return val !== false;
  }).length;

  if (!categoryId || (!loading && fields.length === 0)) return null;

  const chevron = (open) => (
    <span style={{ transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
  );

  const sectionBtn = (id, label, unit) => (
    <button
      onClick={() => toggleSection(id)}
      style={{
        width: '100%', padding: '10px 0', background: 'none', border: 'none',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#333',
      }}
    >
      <span>
        {label}
        {unit && <span style={{ fontSize: '11px', color: '#999', marginLeft: '5px' }}>({unit})</span>}
      </span>
      {chevron(expandedSections.has(id))}
    </button>
  );

  const inputStyle = {
    flex: 1, padding: '10px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '13px',
  };

  return (
    <div style={{
      background: 'white', borderRadius: '12px', padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)', ...style,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #eee',
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }}>
          Filters
          {activeCount > 0 && (
            <span style={{
              marginLeft: '8px', padding: '2px 8px', background: '#ec4899',
              color: 'white', borderRadius: '10px', fontSize: '12px',
            }}>
              {activeCount}
            </span>
          )}
        </h3>
        {activeCount > 0 && (
          <button onClick={onClearAll} style={{
            padding: '6px 12px', background: 'transparent', color: '#ec4899',
            border: '1px solid #ec4899', borderRadius: '6px', cursor: 'pointer',
            fontSize: '12px', fontWeight: '500',
          }}>
            Clear All
          </button>
        )}
      </div>

      {loading && <p style={{ fontSize: '13px', color: '#999', textAlign: 'center' }}>Loading filters…</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {/* Price range — always shown */}
        <div>
          {sectionBtn('price', '💰 Price')}
          {expandedSections.has('price') && (
            <div style={{ padding: '10px 0' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="number" placeholder="Min" style={inputStyle}
                  value={rangeValues['price']?.min || ''}
                  onChange={e => handleRangeChange('price', 'min', parseFloat(e.target.value))} />
                <span style={{ color: '#999' }}>—</span>
                <input type="number" placeholder="Max" style={inputStyle}
                  value={rangeValues['price']?.max || ''}
                  onChange={e => handleRangeChange('price', 'max', parseFloat(e.target.value))} />
              </div>
              <button onClick={() => applyRange('price')} style={{
                width: '100%', marginTop: '10px', padding: '8px', background: '#f0f0f0',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
              }}>Apply</button>
            </div>
          )}
        </div>

        {/* Dynamic category-specific filters */}
        {fields.map(field => (
          <div key={field.id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: '15px' }}>
            {sectionBtn(field.id, field.label, field.unit)}

            {expandedSections.has(field.id) && (
              <div style={{ padding: '10px 0' }}>
                {/* Multi-select / discrete */}
                {(field.filterType === 'multi-select' || field.filterType === 'discrete') && field.options && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    {field.options.map(opt => {
                      const checked = (activeFilters[field.fieldName] || []).includes(opt.value);
                      return (
                        <label key={opt.value} style={{
                          display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                          padding: '6px 8px', borderRadius: '6px',
                          background: checked ? '#fff0f6' : 'transparent',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => handleMultiSelect(field.fieldName, opt.value, e.target.checked)}
                            style={{ width: '16px', height: '16px', accentColor: '#ec4899' }}
                          />
                          <span style={{ flex: 1, fontSize: '13px', color: checked ? '#ec4899' : '#333' }}>
                            {opt.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Range */}
                {field.filterType === 'range' && (
                  <div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input type="number" placeholder={`Min${field.unit ? ' ' + field.unit : ''}`}
                        style={inputStyle}
                        value={rangeValues[field.fieldName]?.min || ''}
                        onChange={e => handleRangeChange(field.fieldName, 'min', parseFloat(e.target.value))} />
                      <span style={{ color: '#999' }}>—</span>
                      <input type="number" placeholder={`Max${field.unit ? ' ' + field.unit : ''}`}
                        style={inputStyle}
                        value={rangeValues[field.fieldName]?.max || ''}
                        onChange={e => handleRangeChange(field.fieldName, 'max', parseFloat(e.target.value))} />
                    </div>
                    <button onClick={() => applyRange(field.fieldName)} style={{
                      width: '100%', marginTop: '10px', padding: '8px', background: '#f0f0f0',
                      border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                    }}>Apply</button>
                  </div>
                )}

                {/* Checkbox (boolean) */}
                {field.filterType === 'checkbox' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '6px 8px' }}>
                    <input
                      type="checkbox"
                      checked={activeFilters[field.fieldName] === true}
                      onChange={e => onFilterChange(field.fieldName, e.target.checked || undefined)}
                      style={{ width: '18px', height: '18px', accentColor: '#ec4899' }}
                    />
                    <span style={{ fontSize: '13px', color: activeFilters[field.fieldName] ? '#ec4899' : '#333' }}>
                      Yes — {field.label}
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{
        marginTop: '20px', padding: '12px', background: '#f8f9fa',
        borderRadius: '8px', fontSize: '11px', color: '#666', textAlign: 'center',
      }}>
        Filters update results in real-time
      </div>
    </div>
  );
};

export default DynamicFilters;
